import type { ElementType, ReactNode } from 'react';

import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-mint text-mint-ink border border-mint hover:bg-mint-hover',
  secondary: 'bg-paper text-ink border border-line hover:bg-mint-tint',
  ghost: 'bg-transparent text-ink border border-transparent hover:bg-mint-tint',
  danger: 'bg-paper text-danger border border-danger hover:bg-danger hover:text-paper',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-9 px-4 text-[13px]',
  md: 'h-11 px-6 text-sm',
};

const baseClasses =
  'inline-flex items-center justify-center gap-2 rounded-pill font-medium ' +
  'transition-colors motion-reduce:transition-none disabled:pointer-events-none disabled:opacity-50 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-paper';

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Render as a different element/component (typically `Link`) while
   * keeping the button look. Extra props (e.g. `to`) pass through. */
  as?: ElementType;
  className?: string;
  children?: ReactNode;
  [extra: string]: unknown;
}

/** The one button in the system — a pill, four variants, per *Maat- en
 * stijlsysteem*. */
export function Button({
  variant = 'primary',
  size = 'md',
  as,
  className,
  children,
  type,
  ...rest
}: ButtonProps) {
  const Component = as ?? 'button';
  const extraProps = Component === 'button' ? { type: type ?? 'button' } : {};

  return (
    <Component
      className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
      {...extraProps}
      {...rest}
    >
      {children}
    </Component>
  );
}
